/**
 * The ECharts theme for ```chart figures, built from the interface's own
 * tokens so a chart reads as part of the reply in every theme: ink on the
 * text tokens, hairline grid on the border token, the tooltip on the popover
 * surface. Series colours follow the validated categorical order (fixed
 * slots, never cycled by rank), magnitude uses one blue ramp and polarity a
 * blue↔red pair through a neutral grey; each mode has its own stepped set.
 *
 * @module components/chat/chartTheme
 */
import { CHART_CATEGORICAL_COLORS } from "@amoreg/shared/chartPalette";

import type { ChartRampName } from "./chartSpec";

export type ChartMode = "light" | "dark";

/** Resolved (rgb/hex) colours; ECharts cannot parse `oklch()` or `var()`. */
export interface ChartTokens {
  readonly foreground: string;
  readonly mutedForeground: string;
  readonly border: string;
  readonly popover: string;
  readonly popoverForeground: string;
  readonly fontFamily: string;
}

// Shared with the interactive HTML pages' `--chart-*` variables.
const CATEGORICAL: Readonly<Record<ChartMode, ReadonlyArray<string>>> = CHART_CATEGORICAL_COLORS;

// Low → high. Low values recede toward the surface in both modes.
const RAMPS: Readonly<Record<ChartMode, Readonly<Record<ChartRampName, ReadonlyArray<string>>>>> = {
  light: {
    sequential: ["#cde2fb", "#86b6ef", "#3987e5", "#1c5cab", "#0d366b"],
    diverging: ["#1c5cab", "#86b6ef", "#f0efec", "#ef9a9a", "#c62f2f"],
  },
  dark: {
    sequential: ["#184f95", "#256abf", "#3987e5", "#6da7ec", "#b7d3f6"],
    diverging: ["#6da7ec", "#256abf", "#383835", "#b84545", "#ef8f8f"],
  },
};

export function chartCategoricalColors(mode: ChartMode): ReadonlyArray<string> {
  return CATEGORICAL[mode];
}

export function chartRamps(mode: ChartMode): Readonly<Record<ChartRampName, ReadonlyArray<string>>> {
  return RAMPS[mode];
}

function axis(tokens: ChartTokens, showSplitLine: boolean) {
  return {
    axisLine: { show: true, lineStyle: { color: tokens.border } },
    axisTick: { show: false },
    axisLabel: { color: tokens.mutedForeground, fontSize: 11 },
    nameTextStyle: { color: tokens.mutedForeground, fontSize: 11 },
    splitLine: { show: showSplitLine, lineStyle: { color: tokens.border, opacity: 0.7 } },
    splitArea: { show: false },
  };
}

export function buildChartTheme(mode: ChartMode, tokens: ChartTokens): Record<string, unknown> {
  const ramps = RAMPS[mode];
  const textStyle = { color: tokens.foreground, fontFamily: tokens.fontFamily };
  return {
    color: [...CATEGORICAL[mode]],
    backgroundColor: "transparent",
    textStyle,
    // Titles that stay in the canvas (several plots in one chart).
    title: {
      textStyle: { color: tokens.foreground, fontSize: 13, fontWeight: 600 },
      subtextStyle: { color: tokens.mutedForeground, fontSize: 12 },
    },
    legend: {
      textStyle: { color: tokens.mutedForeground, fontSize: 12 },
      icon: "circle",
      itemWidth: 8,
      itemHeight: 8,
      itemGap: 14,
      inactiveColor: tokens.border,
      pageTextStyle: { color: tokens.mutedForeground },
    },
    tooltip: {
      backgroundColor: tokens.popover,
      borderColor: tokens.border,
      borderWidth: 1,
      padding: [6, 10],
      textStyle: { color: tokens.popoverForeground, fontSize: 12 },
      axisPointer: {
        lineStyle: { color: tokens.border },
        crossStyle: { color: tokens.border },
        shadowStyle: { color: tokens.border, opacity: 0.25 },
        label: { backgroundColor: tokens.popover, color: tokens.popoverForeground },
      },
    },
    categoryAxis: axis(tokens, false),
    valueAxis: { ...axis(tokens, true), axisLine: { show: false } },
    logAxis: { ...axis(tokens, true), axisLine: { show: false } },
    timeAxis: axis(tokens, false),
    radar: {
      axisName: { color: tokens.mutedForeground },
      axisLine: { lineStyle: { color: tokens.border } },
      splitLine: { lineStyle: { color: tokens.border } },
      splitArea: { show: false },
    },
    visualMap: {
      color: [...ramps.sequential].reverse(),
      textStyle: { color: tokens.mutedForeground, fontSize: 11 },
      itemWidth: 10,
      itemHeight: 120,
    },
    dataZoom: {
      borderColor: tokens.border,
      textStyle: { color: tokens.mutedForeground },
      handleStyle: { color: tokens.popover, borderColor: tokens.mutedForeground },
      moveHandleStyle: { color: tokens.border },
      fillerColor: `${CATEGORICAL[mode][0]}22`,
      dataBackground: {
        lineStyle: { color: tokens.mutedForeground, opacity: 0.4 },
        areaStyle: { color: tokens.mutedForeground, opacity: 0.12 },
      },
    },
    calendar: {
      itemStyle: { color: "transparent", borderColor: tokens.border, borderWidth: 1 },
      splitLine: { lineStyle: { color: tokens.mutedForeground, opacity: 0.5 } },
      dayLabel: { color: tokens.mutedForeground, fontSize: 10 },
      monthLabel: { color: tokens.mutedForeground, fontSize: 11 },
      yearLabel: { color: tokens.mutedForeground },
    },
    // Thin marks: 2px lines, ≥8px markers, rounded bar ends on the value side.
    line: { lineStyle: { width: 2 }, symbolSize: 8, symbol: "circle", showSymbol: false, smooth: false },
    bar: { itemStyle: { borderRadius: [4, 4, 0, 0] }, barMaxWidth: 48 },
    scatter: { symbolSize: 10, itemStyle: { opacity: 0.85 } },
    pie: { itemStyle: { borderColor: tokens.popover, borderWidth: 2 } },
    sankey: {
      label: { color: tokens.foreground },
      lineStyle: { color: "gradient", opacity: 0.35 },
      itemStyle: { borderWidth: 0 },
    },
    treemap: { itemStyle: { borderColor: tokens.popover, gapWidth: 2 } },
    sunburst: { itemStyle: { borderColor: tokens.popover, borderWidth: 2 } },
    graph: {
      lineStyle: { color: tokens.mutedForeground, opacity: 0.5 },
      label: { color: tokens.foreground },
    },
    gauge: {
      axisLine: { lineStyle: { color: [[1, tokens.border]] } },
      axisLabel: { color: tokens.mutedForeground },
      detail: { color: tokens.foreground },
      title: { color: tokens.mutedForeground },
    },
    boxplot: { itemStyle: { borderWidth: 1.5 } },
    candlestick: {
      itemStyle: {
        color: CATEGORICAL[mode][2],
        color0: CATEGORICAL[mode][7],
        borderColor: CATEGORICAL[mode][2],
        borderColor0: CATEGORICAL[mode][7],
      },
    },
  };
}
